import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, MapPin, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { exportSections, logoDataUrl } from "@/lib/exporter";
import { usePoll } from "@/lib/live";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, rupiah, todayStr } from "@/lib/format";

export default function TagihanPage() {
  const { rid } = useParams();
  const { user } = useAuth();
  const { settings, logoSrc } = useSettings();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get(`/resorts/${rid}/tagihan`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load);
  const payPath = ["kasir", "petugas", "admin_cabang"].includes(user.role) ? "/angsuran" : null;
  const pdf = async () => exportSections("pdf", {
    logo: await logoDataUrl(logoSrc), title: `${settings.app_name} — Tagihan Resort ${d.resort.name}`, subtitle: `Per ${fmtDate(todayStr())} · Total sisa ${rupiah(d.total_remaining)}`,
    filename: `tagihan-${d.resort.name}-${todayStr()}`,
    sections: [{ title: `Daftar Tagihan (${d.rows.length} anggota) — Total Sisa Pinjaman ${rupiah(d.total_remaining)}`, head: ["No", "Tanggal Pinjam", "NIK", "Nama Anggota", "Simpanan", "Jumlah Pinjam", "Sisa Pinjaman"], money: [4, 5, 6],
      rows: [...d.rows.map((r, i) => [i + 1, fmtDate(r.loan_date), r.nik, r.name, r.savings_balance, r.principal, r.remaining]), ["", "", "", "TOTAL", d.rows.reduce((s, r) => s + r.savings_balance, 0), d.rows.reduce((s, r) => s + r.principal, 0), d.total_remaining]] }],
  });
  if (!d) return <p className="text-muted-foreground">Memuat...</p>;
  return (
    <div>
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground mb-3" data-testid="tagihan-back"><ArrowLeft className="h-4 w-4 mr-1" />Kembali</button>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div><h1 className="font-heading text-2xl font-bold">Tagihan · {d.resort.name}</h1><p className="text-sm text-muted-foreground">{d.rows.length} anggota · sisa <span className="font-semibold text-primary" data-testid="tagihan-total">{rupiah(d.total_remaining)}</span></p></div>
        <Button variant="outline" className="h-10 rounded-full" onClick={pdf} disabled={!d.rows.length} data-testid="tagihan-pdf"><FileText className="h-4 w-4 mr-1.5" />Cetak PDF</Button>
      </div>
      <div className="panel !p-0 overflow-x-auto" data-testid="tagihan-table">
        {d.rows.length === 0 ? <p className="text-sm text-muted-foreground p-10 text-center">Tidak ada pinjaman berjalan di resort ini.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead className="pl-4">No</TableHead><TableHead>Tanggal Pinjam</TableHead><TableHead>NIK</TableHead><TableHead>Nama Anggota</TableHead><TableHead className="text-right">Simpanan</TableHead><TableHead className="text-right">Jumlah Pinjam</TableHead><TableHead className="text-right">Sisa Pinjaman</TableHead><TableHead className="pr-4">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.rows.map((r, i) => (
                <TableRow key={r.loan_id} data-testid={`tagihan-row-${r.loan_id}`}>
                  <TableCell className="pl-4 font-mono">{i + 1}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(r.loan_date)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.nik}</TableCell>
                  <TableCell className="font-medium whitespace-nowrap">{r.name}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{rupiah(r.savings_balance)}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{rupiah(r.principal)}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap font-semibold">{rupiah(r.remaining)}</TableCell>
                  <TableCell className="pr-4">
                    <div className="flex gap-1.5">
                      {payPath && <Button size="sm" className="h-8 rounded-full" onClick={() => navigate(payPath, { state: { nik: r.nik, loan_id: r.loan_id } })} data-testid={`tagihan-pay-${r.loan_id}`}><Receipt className="h-3.5 w-3.5 mr-1" />Angsuran</Button>}
                      <Button asChild size="sm" variant="outline" className="h-8 rounded-full" disabled={r.lat == null}>
                        <a href={r.lat != null ? `https://www.google.com/maps?q=${r.lat},${r.lng}` : `https://www.google.com/maps/search/${encodeURIComponent(r.address || "")}`} target="_blank" rel="noreferrer" data-testid={`tagihan-maps-${r.loan_id}`}><MapPin className="h-3.5 w-3.5 mr-1" />Maps</a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 font-semibold"><TableCell colSpan={6} className="pl-4 text-right">Total Sisa Pinjaman</TableCell><TableCell className="text-right font-mono">{rupiah(d.total_remaining)}</TableCell><TableCell /></TableRow>
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
